import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, createRefund, getMe, getTicketQr } from '../lib/api-client';
import { useI18n } from '../lib/i18n-context';
import { rememberRefund } from '../lib/refund-history';

/**
 * 退票申請頁 — `/tickets/:id/refund`（AC-B2-4 / AC-B2-5）。
 *
 * - 入口：TicketQR 頁 conditional 按鈕（valid + REFUND_ELIGIBLE）
 * - 顯示票券資訊（state + risk class）+ reason textarea（1..500 字）
 * - `Idempotency-Key` 於 component mount 時 crypto.randomUUID 生成，
 *   送出失敗（含 4xx/5xx/網路）重試使用同 key → BE 同 key 回同 refund_request_id
 * - 成功 → navigate `/refunds/:id`
 */
export function RefundRequestPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [reason, setReason] = useState('');

  // 同一頁面生命週期內固定一個 idempotency key — 重送等效於同一邏輯請求
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);

  const ticketQuery = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => {
      if (id === undefined) throw new Error('missing ticket id');
      return getTicketQr(id);
    },
    enabled: id !== undefined,
    staleTime: 5_000,
  });

  // 取 user_id 給 refund-history namespace（同 ticket-history 模式）
  const meQuery = useQuery({ queryKey: ['auth', 'me'], queryFn: getMe, retry: false });

  const submitMutation = useMutation({
    mutationFn: () => {
      if (id === undefined) throw new Error('missing ticket id');
      return createRefund({ ticket_id: id, reason: reason.trim() }, idempotencyKey);
    },
    onSuccess: (res) => {
      rememberRefund(meQuery.data?.user_id, { refundId: res.id, ticketId: res.ticket_id });
      navigate(`/refunds/${res.id}`);
    },
  });

  const trimmed = reason.trim();
  const canSubmit =
    ticketQuery.isSuccess &&
    ticketQuery.data.ticket_state === 'valid' &&
    ticketQuery.data.refund_risk_class === 'REFUND_ELIGIBLE' &&
    trimmed.length >= 1 &&
    trimmed.length <= 500 &&
    !submitMutation.isPending;

  return (
    <div className="app-shell">
      <header className="topbar">
        <h1>{t('refundReq.title')}</h1>
        <Link to={id !== undefined ? `/tickets/${id}` : '/'} className="topbar-back">
          {t('refundReq.backToTicket')}
        </Link>
      </header>

      {ticketQuery.isPending && <p>{t('common.loading')}</p>}
      {ticketQuery.isError && (
        <p className="error-text">
          {t('refundReq.loadFailed')}
          {ticketQuery.error instanceof ApiError
            ? (ticketQuery.error.problem?.detail ?? ticketQuery.error.message)
            : t('common.unknownError')}
        </p>
      )}

      {ticketQuery.isSuccess && (
        <article className="card">
          <p style={{ fontSize: 14, color: '#555' }}>
            {t('refundReq.ticketLabel')}
            <code>{ticketQuery.data.ticket_code}</code>
            <span style={{ marginLeft: 8 }}>
              {t('refundReq.statusInline', { state: ticketQuery.data.ticket_state })}
            </span>
          </p>

          {ticketQuery.data.ticket_state !== 'valid' && (
            <p className="error-text">
              {t('refundReq.cannotRefundState', { state: ticketQuery.data.ticket_state })}
            </p>
          )}
          {ticketQuery.data.ticket_state === 'valid' &&
            ticketQuery.data.refund_risk_class !== 'REFUND_ELIGIBLE' && (
              <p className="error-text">{t('refundReq.lockedWindow')}</p>
            )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) {
                submitMutation.mutate();
              }
            }}
          >
            <label style={{ display: 'block', marginTop: 12, fontSize: 14 }}>
              {t('refundReq.reasonLabel')}
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={6}
                maxLength={500}
                style={{
                  display: 'block',
                  width: '100%',
                  marginTop: 6,
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid var(--boat-border)',
                  fontSize: 14,
                  resize: 'vertical',
                }}
                placeholder={t('refundReq.reasonPlaceholder')}
              />
            </label>
            <p style={{ fontSize: 12, color: '#777', marginTop: 4, textAlign: 'right' }}>
              {t('refundReq.charCount', { count: trimmed.length })}
            </p>

            {submitMutation.isError && (
              <p className="error-text">
                {t('refundReq.submitFailed')}
                {submitMutation.error instanceof ApiError
                  ? (submitMutation.error.problem?.detail ?? submitMutation.error.message)
                  : t('common.unknownError')}
              </p>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="btn-primary btn-block"
              style={{ marginTop: 12 }}
            >
              {submitMutation.isPending ? t('refundReq.submitting') : t('refundReq.submit')}
            </button>
          </form>

          <p style={{ fontSize: 12, color: '#777', marginTop: 16 }}>{t('refundReq.footnote')}</p>
        </article>
      )}
    </div>
  );
}
